import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import clsx from 'clsx';
import dayjs from 'dayjs';
import TextareaAutosize from 'react-textarea-autosize';
import { useTypedSelector } from '@/store';
import MaskedTimeInput from '@/components/MaskedTimeInput.tsx';

import TagsSelect from '@/components/TagsSelect';

import { getIsMobileScreen } from '@/store/core/selectors';

type Props = {
  value: any;
  type: 'name' | 'description' | 'dueDate' | 'dueTime' | 'tags';
  onChange: (field: 'name' | 'description' | 'dueDate' | 'dueTime' | 'tags', val: any) => void;
  placeholder?: string;
};

const ImplicitInput: React.FC<Props> = ({ value, type, onChange, placeholder }) => {
  const isMobileScreen = useTypedSelector(state => getIsMobileScreen(state));

  const [intermediateValue, setIntermediateValue] = useState<any>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [maxRows, setMaxRows] = useState<number>(3);

  const onChangeValue = (field: 'name' | 'description' | 'dueDate' | 'dueTime' | 'tags', val: any) => {
    if (field !== 'tags') {
      setIsFocused(false);
    }
    if (val === value) return;
    if (field === 'dueTime' && !dayjs(val, 'HH:mm', true).isValid()) return;

    onChange(field, val);
  };

  useEffect(() => {
    if (!isFocused) {
      setIntermediateValue(value);
    }
  }, [isFocused, value, type]);

  useEffect(() => {
    setTimeout(() => {
      setMaxRows(6);
    }, 300);
  }, []);

  // Function to parse DD.MM.YYYY string to Date object
  const parseDate = (dateStr: string) => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split('.');
    if (!day || !month || !year) return null;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  // Function to format Date to DD.MM.YYYY string
  const formatDate = (date: Date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <>
      {type === 'name' && (
        <TextareaAutosize
          minRows={1}
          maxRows={maxRows}
          className={clsx('task-view__name', {
            'task-view__name--inactive': !isFocused,
          })}
          onFocus={() => setIsFocused(true)}
          value={intermediateValue}
          onChange={e => setIntermediateValue(e.target.value)}
          onBlur={() => onChangeValue('name', intermediateValue)}
        />
      )}

      {type === 'description' && (
        <TextareaAutosize
          minRows={1}
          maxRows={maxRows}
          className={clsx('task-view__description', {
            'task-view__description--inactive': !isFocused,
          })}
          placeholder={placeholder}
          onFocus={() => setIsFocused(true)}
          value={intermediateValue || ''}
          onChange={e => setIntermediateValue(e.target.value)}
          onBlur={() => onChangeValue('description', intermediateValue)}
        />
      )}

      {type === 'dueTime' && (
        <MaskedTimeInput
          className={clsx('task-view__duetime', {
            'task-view__duetime--inactive': !isFocused,
          })}
          placeholder={placeholder}
          onFocus={() => setIsFocused(true)}
          value={intermediateValue}
          onChange={value => setIntermediateValue(value)}
          onBlur={() => onChangeValue('dueTime', intermediateValue)}
        />
      )}

      {type === 'dueDate' && (
        <div className={clsx('task-view__datepicker-container', {
          'task-view__datepicker-container--focused': isFocused,
        })}>
          <DatePicker
            className={clsx('task-view__datepicker', {
              'task-view__datepicker--inactive': !isFocused,
            })}
            calendarClassName="green-theme-calendar"
            dayClassName={() => "green-theme-day"}
            placeholderText={placeholder}
            selected={parseDate(intermediateValue)}
            dateFormat="dd.MM.yyyy"
            onChange={(date: Date) => onChangeValue('dueDate', formatDate(date))}
            onCalendarOpen={() => setIsFocused(true)}
            onCalendarClose={() => setIsFocused(false)}
            onBlur={() => setIsFocused(false)}
            popperPlacement="bottom-start"
            readOnly={isMobileScreen}
            onClickOutside={(e) => {
              e.preventDefault();
            }}
            shouldCloseOnSelect={true}
          />
        </div>
      )}

      {type === 'tags' && (
        <TagsSelect
          className={clsx('task-view__tags-select', {
            'task-view__tags-select--inactive': !isFocused,
          })}
          isFromTaskView
          placeholder="Project"
          onFocus={() => setIsFocused(true)}
          value={value}
          onChange={val => onChangeValue('tags', val)}
          onBlur={() => setIsFocused(false)}
        />
      )}
    </>
  );
};

export default ImplicitInput;
